import React, { useState, useEffect } from 'react';
import { FaSpinner, FaCalendar, FaUser } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/apiConfig';
import './MediaSection.css';

const FALLBACK_MEDIA = [
  {
    _id: 1,
    title: "5 Tips for Finding the Best Cab Service",
    description: "Learn how to choose the right cab service for your daily commute. We share insider tips on what makes a great ride experience.",
    slug: "5-tips-best-cab-service",
    category: "Travel Tips",
    featuredImage: "https://images.unsplash.com/photo-1500595046891-13d9a48c2c44?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=90",
    authorName: "Rajesh Kumar",
    authorImage: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=200&q=90",
    publishDate: "2024-01-15"
  },
  {
    _id: 2,
    title: "Safety First: Your Guide to Secure Rides",
    description: "Safety is our top priority. Discover the comprehensive safety measures in place to protect you on every journey.",
    slug: "safety-first-secure-rides",
    category: "Safety",
    featuredImage: "https://images.unsplash.com/photo-1519904981063-b0cf448d479e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=90",
    authorName: "Priya Sharma",
    authorImage: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=200&q=90",
    publishDate: "2024-01-20"
  },
  {
    _id: 3,
    title: "How to Save Money on Daily Commutes",
    description: "Discover smart ways to save on your daily rides. Our money-saving tips can help you cut commute costs significantly.",
    slug: "save-money-daily-commutes",
    category: "Budget Tips",
    featuredImage: "https://images.unsplash.com/photo-1566776615325-7c321b0b5a7d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=90",
    authorName: "Amit Patel",
    authorImage: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=200&q=90",
    publishDate: "2024-01-22"
  },
  {
    _id: 4,
    title: "CabZigo Expands to 10 New Cities",
    description: "We are thrilled to announce our expansion into 10 more cities across India, bringing comfortable travel to even more people.",
    slug: "cabzigo-expands-10-new-cities",
    category: "News",
    featuredImage: "https://images.unsplash.com/photo-1517733994350-d41e7ceeb115?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=90",
    authorName: "Karan Singh",
    authorImage: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=90",
    publishDate: "2024-02-01"
  },
  {
    _id: 5,
    title: "Eco-Friendly Rides: Our New Electric Fleet",
    description: "Join us in our journey towards sustainability. CabZigo introduces a brand new fleet of electric vehicles for a greener future.",
    slug: "eco-friendly-electric-fleet",
    category: "Sustainability",
    featuredImage: "https://images.unsplash.com/photo-1593941707882-a5bba14938c7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=90",
    authorName: "Neha Sharma",
    authorImage: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=90",
    publishDate: "2024-02-10"
  }
];

const MediaSection = () => {
  const [media, setMedia] = useState(FALLBACK_MEDIA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_URL}/media/public`, { params: { limit: 12 } });

      if (response.data.success && response.data.data && response.data.data.length > 0) {
        setMedia(response.data.data);
      } else {
        // Use fallback if no media returned
        setMedia(FALLBACK_MEDIA);
      }
    } catch (error) {
      console.warn('Error fetching media, using fallback data:', error?.message);
      // Use fallback on any error
      setMedia(FALLBACK_MEDIA);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
   <section id="media-section" className="bg-white text-gray-900 py-8 md:py-20">
  <div className="max-w-[1180px] mx-auto px-6">

    {/* Heading */}
    <div className="text-center mb-6 md:mb-14">
      <h2 className="text-4xl font-bold">
        <span className="text-blue-500">Cab</span>
        <span className="text-orange-500">Zigo</span>
        <span className="text-black" > in media</span>
      </h2>
      <div className="w-60 h-2 bg-gradient-to-r from-gray-200 to-blue-600 rounded-full mx-auto mt-3"></div>
    </div>

    {/* Loading */}
    {loading && (
      <div className="flex justify-center py-16">
        <FaSpinner className="animate-spin text-blue-500 text-4xl" />
      </div>
    )}

    {/* Error */}
    {error && (
      <div className="text-center text-red-500 py-10">
        {error}
      </div>
    )}

    {/* Media Grid */}
    {!loading && !error && media.length > 0 && (
      <div className="grid md:grid-cols-3 gap-10">

        {media.map((item) => (
          <div key={item._id} className="group flex flex-col h-full bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden border border-gray-100">

            {/* Image */}
            <Link to={`/media/${item.slug || item._id}`} className="relative block overflow-hidden">
              <img
                src={item.featuredImage}
                alt={item.title}
                className="w-full h-64 object-cover group-hover:scale-105 transition duration-500"
                onError={(e) => {
                  e.target.src =
                    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect width='100%25' height='100%25' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='18' fill='%236b7280'%3EMedia Image%3C/text%3E%3C/svg%3E";
                }}
              />

              {/* Category Badge */}
              <span className="absolute top-4 right-4 bg-black/40 backdrop-blur-md px-4 py-1 text-white text-xs font-bold rounded-md border border-white/20">
                {item.category}
              </span>
            </Link>

            <div className="p-6 flex flex-col flex-grow">
              {/* Author */}
              <div className="flex items-center gap-3">
                <img
                  src={item.authorImage}
                  alt={item.authorName}
                  className="w-10 h-10 rounded-full object-cover border border-gray-100"
                  onError={(e) => {
                    e.target.src =
                      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Crect width='100%25' height='100%25' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='10' fill='%236b7280'%3EAuthor%3C/text%3E%3C/svg%3E";
                  }}
                />
                <div>
                  <p className="font-bold text-gray-800 text-sm">
                    {item.authorName}
                  </p>
                  <p className="text-gray-400 text-xs">
                    {formatDate(item.publishDate)}
                  </p>
                </div>
              </div>

              {/* Title */}
              <h3 className="text-lg font-extrabold text-gray-900 mt-4 group-hover:text-blue-600 transition duration-200 line-clamp-2">
                <Link to={`/media/${item.slug || item._id}`}>
                  {item.title}
                </Link>
              </h3>

              {/* Description */}
              <p className="text-gray-500 mt-3 text-sm leading-relaxed line-clamp-3 flex-grow">
                {item.description}
              </p>

              {/* Read Link */}
              <div className="mt-6 pt-4 border-t border-gray-50">
                <Link to={`/media/${item.slug || item._id}`} className="inline-flex items-center text-xs font-extrabold text-blue-600 hover:text-blue-800 uppercase tracking-wider transition-colors duration-200">
                  Read Full Story <span className="ml-1 transition-transform group-hover:translate-x-1">→</span>
                </Link>
              </div>
            </div>

          </div>
        ))}

      </div>
    )}

    {/* Empty */}
    {!loading && !error && media.length === 0 && (
      <div className="text-center py-12 text-gray-400">
        No media items available.
      </div>
    )}

    {/* Navigation Arrows */}
    <div className="flex gap-4 mt-12">
      <button className="w-14 h-10 bg-gray-300  flex items-center justify-center hover:bg-gray-700 transition text-white">
        ←
      </button>
      <button className="w-14 h-10 bg-gray-800  flex items-center justify-center hover:bg-gray-700 transition text-white">
        →
      </button>
    </div>

  </div>
</section>
  );
};

export default MediaSection;
